/*
 * Titles, meta descriptions, headings and intro text for public pages, in the
 * three site languages. Shared by the API's page renderer and the UI (through
 * the same webpack alias as urls.js), so what crawlers get in the HTML and
 * what React sets afterwards are identical.
 *
 * Every function returns plain text; callers escape it for HTML.
 */
const {
  governorateName, lawyerName, findGovernorate, findBarLevel, findCategory, categoryName, LANGS,
} = require("./urls");

const SITE_NAME = "AvoConsulte";
const lang3 = (lang) => (LANGS.includes(lang) ? lang : "ar");
const pick = (lang, byLang) => byLang[lang3(lang)];
// Dates are stored at midnight UTC (scripts/import-onat.js): read the year in UTC.
const year = (date) => (date ? new Date(date).getUTCFullYear() : null);
// Values the schema forces on lawyers that have no real one ("-", "NULL").
const PLACEHOLDERS = new Set(["", "-", "null", "undefined", "0"]);
const realText = (value) => (typeof value === "string" && !PLACEHOLDERS.has(value.trim().toLowerCase()) ? value.trim() : "");
const lcFirst = (text) => text.charAt(0).toLowerCase() + text.slice(1);
// "à Sfax", but "au Kef".
const frAt = (city) => (/^Le /.test(city) ? `au ${city.slice(3)}` : `à ${city}`);
const frOf = (city) => (/^Le /.test(city) ? `du ${city.slice(3)}` : `de ${city}`);

// Meta descriptions are cut around 155-160 characters in results.
function clip(text, max = 158) {
  const clean = String(text || "").replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/\s+\S*$/, "")}…`;
}

// " - page 2" on the title and meta description of a listing's later pages,
// so that no two pages of a listing share them.
function pageSuffix(lang, page) {
  const n = Number(page);
  if (!Number.isInteger(n) || n < 2) return "";
  return pick(lang, { ar: ` - الصفحة ${n}`, fr: ` - page ${n}`, en: ` - page ${n}` });
}

// A meta description ending with the page suffix, the whole within clip()'s limit.
function pagedDescription(text, suffix) {
  return suffix ? `${clip(text, 158 - suffix.length)}${suffix}` : clip(text);
}

/** Title, h1, meta description and intro (the description without the page) of a paginated listing. */
function listing(lang, page, { title, h1, description }) {
  const suffix = pageSuffix(lang, page);
  return { title: `${title}${suffix} | ${SITE_NAME}`, h1, description: pagedDescription(description, suffix), intro: description };
}

function stripHtml(html) {
  return String(html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();
}

function home(lang) {
  return pick(lang, {
    ar: {
      title: `${SITE_NAME} | دليل المحامين في تونس واستشارات قانونية`,
      description: "ابحث عن محامٍ في ولايتك من بين آلاف المحامين المرسّمين بالهيئة الوطنية للمحامين بتونس، واطرح سؤالك القانوني واقرأ مقالات المجلة القانونية.",
      h1: "دليل المحامين في تونس",
      intro: "ابحث عن محامٍ حسب الولاية ودرجة الترسيم، واطّلع على عنوان مكتبه ورقم هاتفه، أو اطرح سؤالك القانوني مجانًا.",
    },
    fr: {
      title: `${SITE_NAME} | Annuaire des avocats en Tunisie et conseil juridique`,
      description: "Trouvez un avocat dans votre gouvernorat parmi les avocats inscrits à l'Ordre National des Avocats de Tunisie, posez votre question juridique et lisez notre magazine.",
      h1: "Annuaire des avocats en Tunisie",
      intro: "Recherchez un avocat par gouvernorat et par tableau, consultez l'adresse et le téléphone de son cabinet, ou posez gratuitement votre question juridique.",
    },
    en: {
      title: `${SITE_NAME} | Directory of lawyers in Tunisia and legal advice`,
      description: "Find a lawyer in your governorate among the lawyers registered with the Tunisian Bar, ask your legal question and read our legal magazine.",
      h1: "Directory of lawyers in Tunisia",
      intro: "Search lawyers by governorate and bar level, see their office address and phone number, or ask your legal question for free.",
    },
  });
}

/** City / city x bar-level listing. gov and level may be objects or any spelling. */
function lawyers(lang, { governorate, barLevel, total, page } = {}) {
  const l = lang3(lang);
  const gov = governorate && (governorate.key ? governorate : findGovernorate(governorate));
  const level = barLevel && (barLevel.slug ? barLevel : findBarLevel(barLevel));
  const city = gov ? governorateName(gov, l) : null;
  const count = typeof total === "number" && total > 0 ? total : null;
  const suffix = pageSuffix(l, page);

  let h1;
  let description;
  if (gov && level) {
    h1 = pick(l, {
      ar: `${level.plural.ar} في ${city}`,
      fr: `${level.plural.fr} ${frAt(city)}`,
      en: `${level.plural.en} in ${city}`,
    });
    description = pick(l, {
      ar: `${h1}${count ? ` (${count})` : ""}: العناوين وأرقام الهاتف وتاريخ أداء اليمين. ابحث عن المحامي المناسب في ${city}.`,
      fr: `${h1}${count ? ` (${count})` : ""} : adresses, téléphones et date de prestation de serment. Trouvez l'avocat qu'il vous faut ${frAt(city)}.`,
      en: `${h1}${count ? ` (${count})` : ""}: office addresses, phone numbers and date sworn in. Find the right lawyer in ${city}.`,
    });
  } else if (gov) {
    h1 = pick(l, { ar: `محامون في ${city}`, fr: `Avocats ${frAt(city)}`, en: `Lawyers in ${city}` });
    description = pick(l, {
      ar: `دليل ${count ? `${count} ` : ""}محامٍ في ولاية ${city}: العنوان ورقم الهاتف ودرجة الترسيم (تعقيب، استئناف، متربص). اختر محاميك في ${city}.`,
      fr: `Annuaire de ${count ? `${count} ` : ""}avocats dans le gouvernorat de ${city} : adresse, téléphone et tableau (cassation, appel, stagiaire). Choisissez votre avocat ${frAt(city)}.`,
      en: `Directory of ${count ? `${count} ` : ""}lawyers in ${city} governorate: address, phone and bar level (cassation, appeal, trainee). Choose your lawyer in ${city}.`,
    });
  } else if (level) {
    // A bar level across the whole country: a search filter on the site, not
    // an indexed page (those always name a city), so it gets no URL of its own.
    h1 = pick(l, {
      ar: `${level.plural.ar} في كامل تراب الجمهورية`,
      fr: `${level.plural.fr} en Tunisie`,
      en: `${level.plural.en} in Tunisia`,
    });
    description = pick(l, {
      ar: `${h1}${count ? ` (${count})` : ""}: العناوين وأرقام الهاتف وتاريخ أداء اليمين.`,
      fr: `${h1}${count ? ` (${count})` : ""} : adresses, téléphones et date de prestation de serment.`,
      en: `${h1}${count ? ` (${count})` : ""}: office addresses, phone numbers and date sworn in.`,
    });
  } else {
    h1 = pick(l, { ar: "المحامون في تونس", fr: "Les avocats en Tunisie", en: "Lawyers in Tunisia" });
    description = pick(l, {
      ar: "دليل المحامين في تونس حسب الولاية: تونس، صفاقس، سوسة، نابل وغيرها. العناوين وأرقام الهاتف ودرجة الترسيم.",
      fr: "Annuaire des avocats en Tunisie par gouvernorat : Tunis, Sfax, Sousse, Nabeul et plus. Adresses, téléphones et tableau.",
      en: "Directory of lawyers in Tunisia by governorate: Tunis, Sfax, Sousse, Nabeul and more. Addresses, phone numbers and bar level.",
    });
  }
  return {
    title: `${h1}${suffix} | ${SITE_NAME}`,
    description: pagedDescription(description, suffix),
    h1,
    intro: description,
  };
}

/** Lawyer profile. `lawyer` is a Lawyer document (or its JSON). */
function lawyer(lang, doc) {
  const l = lang3(lang);
  const name = lawyerName(doc, l);
  const gov = findGovernorate(doc.governorateKey) || findGovernorate(doc.stateInFrench) || findGovernorate(doc.stateInArabic);
  const city = gov ? governorateName(gov, l) : realText(l === "ar" ? doc.stateInArabic : doc.stateInFrench);
  const level = findBarLevel(doc.barLevel);
  const sworn = year(doc.swornInOn);
  const levelLabel = level ? level[l] : pick(l, { ar: "محامٍ", fr: "Avocat", en: "Lawyer" });

  const title = pick(l, {
    ar: `الأستاذ ${name} - ${levelLabel}${city ? ` في ${city}` : ""} | ${SITE_NAME}`,
    fr: `Maître ${name} - ${levelLabel}${city ? ` ${frAt(city)}` : ""} | ${SITE_NAME}`,
    en: `${name} - ${levelLabel}${city ? ` in ${city}` : ""} | ${SITE_NAME}`,
  });

  const about = pick(l, {
    ar: [
      `الأستاذ ${name} ${levelLabel}${city ? ` بولاية ${city}` : ""}، مرسّم بالهيئة الوطنية للمحامين بتونس.`,
      sworn ? `أدّى اليمين سنة ${sworn}.` : null,
      doc.barSection && doc.barSection.ar ? `ينتمي إلى فرع ${doc.barSection.ar}.` : null,
    ],
    fr: [
      `Maître ${name} est ${lcFirst(levelLabel)}${city ? ` ${frAt(city)}` : ""}, inscrit à l'Ordre National des Avocats de Tunisie.`,
      sworn ? `Prestation de serment en ${sworn}.` : null,
      doc.barSection && doc.barSection.fr ? `Section régionale ${frOf(doc.barSection.fr)}.` : null,
    ],
    en: [
      `${name} is a ${/^Trainee/.test(levelLabel) ? lcFirst(levelLabel) : levelLabel}${city ? ` in ${city}` : ""}, registered with the Tunisian Bar (ONAT).`,
      sworn ? `Sworn in ${sworn}.` : null,
      doc.barSection && doc.barSection.fr ? `Regional section: ${doc.barSection.fr}.` : null,
    ],
  }).filter(Boolean).join(" ");

  const own = stripHtml(doc.aboutMe);
  return {
    title,
    description: clip(own ? `${about} ${own}` : `${about} ${pick(l, {
      ar: "العنوان ورقم الهاتف على AvoConsulte.",
      fr: "Adresse et téléphone du cabinet sur AvoConsulte.",
      en: "Office address and phone number on AvoConsulte.",
    })}`),
    h1: pick(l, { ar: `الأستاذ ${name}`, fr: `Maître ${name}`, en: name }),
    about,
    levelLabel,
    city,
  };
}

function questions(lang, { page } = {}) {
  return listing(lang, page, pick(lang, {
    ar: { title: "استشارات قانونية مجانية", h1: "استشارات قانونية", description: "أسئلة قانونية طرحها مواطنون تونسيون وأجاب عنها محامون: الطلاق، الشغل، الكراء، الميراث وغيرها. اطرح سؤالك مجانًا." },
    fr: { title: "Questions juridiques gratuites", h1: "Questions - Réponses juridiques", description: "Questions juridiques posées par des Tunisiens et répondues par des avocats : divorce, travail, bail, succession et plus. Posez votre question gratuitement." },
    en: { title: "Free legal questions", h1: "Legal questions and answers", description: "Legal questions asked by people in Tunisia and answered by lawyers: divorce, employment, rent, inheritance and more. Ask yours for free." },
  }));
}

function question(lang, q) {
  const text = stripHtml(q.description || q.content);
  return {
    title: `${q.title} | ${SITE_NAME}`,
    description: clip(text || questions(lang).description),
    h1: q.title,
  };
}

function blogs(lang, { page } = {}) {
  return listing(lang, page, pick(lang, {
    ar: { title: "المجلة القانونية", h1: "المجلة القانونية", description: "مقالات وأدلة قانونية مبسّطة حول القانون التونسي: الأسرة، الشغل، الكراء، العقود، وكيف تختار محاميك." },
    fr: { title: "Magazine juridique", h1: "Magazine juridique", description: "Articles et guides juridiques sur le droit tunisien : famille, travail, bail, contrats, et comment choisir son avocat." },
    en: { title: "Legal magazine", h1: "Legal magazine", description: "Plain-language articles and guides on Tunisian law: family, employment, rent, contracts, and how to choose a lawyer." },
  }));
}

function blog(lang, b) {
  return {
    title: `${b.title} | ${SITE_NAME}`,
    description: clip(b.description || stripHtml(b.body)),
    h1: b.title,
  };
}

/** Magazine topic page: its guides and the editorial team's answers to common questions. */
function category(lang, cat) {
  const l = lang3(lang);
  const topic = cat && (cat.slug ? cat : findCategory(cat));
  const name = topic ? categoryName(topic, l) : "";
  const h1 = pick(l, {
    ar: `${name}: أدلة وأسئلة شائعة`,
    fr: `${name} : guides et questions fréquentes`,
    en: `${name}: guides and FAQ`,
  });
  const intro = pick(l, {
    ar: `كل الأدلة العملية والأسئلة الشائعة في باب «${name}» حول القانون التونسي، من إعداد فريق تحرير AvoConsulte. هي معلومات عامة ولا تغني عن استشارة محامٍ يدرس وضعيتك الخاصة.`,
    fr: `Tous nos guides pratiques et les réponses aux questions fréquentes de la rubrique « ${name} » en droit tunisien, rédigés par l'équipe éditoriale d'AvoConsulte. Ce sont des informations générales : pour votre situation, consultez un avocat.`,
    en: `All our practical guides and answers to frequently asked questions in the "${name}" section on Tunisian law, written by the AvoConsulte editorial team. This is general information: for your own situation, consult a lawyer.`,
  });
  return {
    title: `${h1} | ${SITE_NAME}`,
    description: clip(intro),
    h1,
    intro,
    name,
  };
}

function page(lang, type) {
  const pages = {
    about: {
      ar: { title: `من نحن | ${SITE_NAME}`, h1: "من نحن", description: "AvoConsulte منصة تونسية تربط المواطنين بالمحامين: دليل المحامين، استشارات قانونية ومجلة قانونية." },
      fr: { title: `Qui sommes-nous | ${SITE_NAME}`, h1: "Qui sommes-nous", description: "AvoConsulte est une plateforme tunisienne qui met en relation citoyens et avocats : annuaire, questions juridiques et magazine." },
      en: { title: `About us | ${SITE_NAME}`, h1: "About us", description: "AvoConsulte is a Tunisian platform connecting people with lawyers: lawyer directory, legal questions and a legal magazine." },
    },
    contact: {
      ar: { title: `اتصل بنا | ${SITE_NAME}`, h1: "اتصل بنا", description: "تواصل مع فريق AvoConsulte لأي سؤال أو لتحديث ملفك كمحامٍ." },
      fr: { title: `Contactez-nous | ${SITE_NAME}`, h1: "Contactez-nous", description: "Contactez l'équipe AvoConsulte pour toute question ou pour mettre à jour votre profil d'avocat." },
      en: { title: `Contact us | ${SITE_NAME}`, h1: "Contact us", description: "Contact the AvoConsulte team with any question or to update your lawyer profile." },
    },
    terms: {
      ar: { title: `الخصوصية والأمن | ${SITE_NAME}`, h1: "الخصوصية والأمن", description: "سياسة الخصوصية وشروط استخدام منصة AvoConsulte." },
      fr: { title: `Confidentialité | ${SITE_NAME}`, h1: "Confidentialité", description: "Politique de confidentialité et conditions d'utilisation d'AvoConsulte." },
      en: { title: `Privacy | ${SITE_NAME}`, h1: "Privacy", description: "Privacy policy and terms of use of AvoConsulte." },
    },
    notFound: {
      ar: { title: `الصفحة غير موجودة | ${SITE_NAME}`, h1: "الصفحة غير موجودة", description: "" },
      fr: { title: `Page introuvable | ${SITE_NAME}`, h1: "Page introuvable", description: "" },
      en: { title: `Page not found | ${SITE_NAME}`, h1: "Page not found", description: "" },
    },
  };
  return pick(lang, pages[type] || pages.notFound);
}

module.exports = {
  SITE_NAME,
  clip,
  stripHtml,
  realText,
  home,
  lawyers,
  lawyer,
  questions,
  question,
  blogs,
  blog,
  category,
  page,
};
